import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useLabels from "../../hooks/useLabels";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useUserResources } from "../../hooks/useUserResources";
import { selectActiveWinery, selectConfigs, selectUser } from "../../context/selectors";
import { getItem } from "../../services/LocalStorage";
import { ROLES } from "../../constants/base";
import { CTA, Card } from "../Generic";
import { Icon } from "../Icon";
import { SkeletonNextOperations } from "../Skeleton/Skeleton";
import { getEventAlertsSummary } from "../../services/eventAlerts";
import { buildAlertPresentation } from "../../services/eventAlertsPresenter";
import { EVENT_ALERT_LABEL_KEYS as KEYS } from "../../constants/eventAlertLabels";
import "./styles/index.scss";

const MAX_ITEMS = 5;

// Read-only Home widget: gated on role + the ALERTS resource (role alone is
// never enough - see FE2 instructions). Reuses the FE1 client/presenter as-is,
// no local title/message/deep-link logic and no workflow actions here.
const EventAlertsHomeWidget = () => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const user = useSelector(selectUser);
  const activeWinery = useSelector(selectActiveWinery);
  const activeWineryId = activeWinery?.id || getItem("activeWineryId");
  const configs = useSelector(selectConfigs);
  const userResources = useUserResources(user, activeWineryId);
  const axiosPrivate = useAxiosPrivate();

  const [status, setStatus] = useState("loading");
  const [summary, setSummary] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const isSupportedRole = role === ROLES.CLIENT || role === ROLES.USER;
  const canView = isSupportedRole && !!userResources["ALERTS"]?.read;

  useEffect(() => {
    if (!canView) return;

    let isMounted = true;
    setStatus("loading");

    (async () => {
      try {
        const data = await getEventAlertsSummary(axiosPrivate);
        if (!isMounted) return;
        setSummary(data);
        setStatus("success");
      } catch (e) {
        if (!isMounted) return;
        setStatus("error");
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [canView, activeWineryId, reloadToken]);

  if (!canView) return null;

  const title = getLabel(
    role === ROLES.CLIENT ? "eventAlertsHomeClientTitle" : "eventAlertsHomeUserTitle"
  );

  return (
    <div className="primary_container_upper_info">
      <h4>{title}</h4>

      {/* Hidden only on error, matching this widget's existing error state
          (message + retry only, no other affordances). */}
      {status !== "error" && (
        <Link to="/alerts" className="event_alerts_view_all_link">
          {getLabel(KEYS.VIEW_ALL)}
        </Link>
      )}

      {status === "success" && (
        <h5>
          {getLabel("eventAlertsActiveCount")}: {summary?.active ?? 0}
          {" — "}
          {getLabel("eventAlertsCriticalCount")}: {summary?.critical ?? 0}
          {" — "}
          {getLabel("eventAlertsWarningCount")}: {summary?.warning ?? 0}
        </h5>
      )}

      <Card customClassName={"light_gray"}>
        {status === "loading" && <SkeletonNextOperations rowsCount={3} />}

        {status === "error" && (
          <>
            <h5 className="alert_no_info">{getLabel("eventAlertsLoadError")}</h5>
            <CTA
              customClassName="small"
              onClick={() => setReloadToken((token) => token + 1)}
            >
              <Icon icon="fa-rotate" /> {getLabel("eventAlertsRetry")}
            </CTA>
          </>
        )}

        {status === "success" && summary?.active === 0 && (
          <h5 className="alert_no_info">{getLabel("eventAlertsEmpty")}</h5>
        )}

        {status === "success" && summary?.active > 0 && (
          <div className="next_operations_wrapper">
            {(summary?.items || []).slice(0, MAX_ITEMS).map((alert) => {
              const presentation = buildAlertPresentation(alert, getLabel, {
                dateFormat: configs?.shortDateFormat,
              });
              const rowContent = (
                <div className="expand-width">
                  <Icon
                    icon={presentation.icon}
                    size="lg"
                    className={`event_alert_icon severity_${(presentation.severity || "").toLowerCase()}`}
                  />
                  <span className="event_alert_title">{presentation.title}</span>
                  <div>{presentation.message}</div>
                </div>
              );

              return presentation.deepLink ? (
                <Link
                  key={alert.id}
                  to={presentation.deepLink}
                  className="next_operations_wrapper_operation event_alert_row"
                >
                  {rowContent}
                </Link>
              ) : (
                <div key={alert.id} className="next_operations_wrapper_operation event_alert_row">
                  {rowContent}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventAlertsHomeWidget;
